import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Checkbox } from '../ui/checkbox';
import { Input } from '@/components/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/Select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Label } from '../ui/label';
import { Textarea } from '@/components/ui/Textarea';
import { Alert } from '@/components/ui/alert';
import { Plus, Edit, Trash2, Loader2, Filter, X, Clipboard, Settings, Download, Lock } from 'lucide-react';
import { useTenant } from '@/lib/tenant/tenant-provider';
import {
  listEvaluationItems,
  createEvaluationItem,
  updateEvaluationItem,
  deleteEvaluationItem,
  listEvaluationItemsByRole,
  listEvaluationCategories
} from '@/api/performance';
import { listRoles, listRoleGroups } from '@/api/workforce';
import type {
  EvaluationItem,
  CreateEvaluationItemData,
  UpdateEvaluationItemData,
  EvaluationCategory
} from '@/types/performance';
import type { Role } from '@/types/workforce';

const MatrizItens = () => {
  const { tenantDomain } = useTenant();
  const [itens, setItens] = useState<EvaluationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<EvaluationItem | null>(null);
  const [newItem, setNewItem] = useState<Partial<CreateEvaluationItemData>>({
    category: '',
    description: '',
    weight: 1,
    applicableRoles: [],
    applicableRoleGroups: [],
    status: 'active'
  });

  // Role filtering state
  const [selectedRoleFilter, setSelectedRoleFilter] = useState<string | null>(null);

  // Role group filtering state
  const [roleGroups, setRoleGroups] = useState<string[]>([]);
  const [selectedGroupFilter, setSelectedGroupFilter] = useState<string | null>(null);
  const [loadingGroups, setLoadingGroups] = useState(false);

  // Available roles fetched from workforce API
  const [availableRoles, setAvailableRoles] = useState<Role[]>([]);
  const [loadingRoles, setLoadingRoles] = useState(false);

  // Available categories fetched from performance API
  const [availableCategories, setAvailableCategories] = useState<EvaluationCategory[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  // Fetch evaluation items
  const fetchItens = async (roleId?: string) => {
    if (!tenantDomain) return;

    setLoading(true);
    setError(null);
    try {
      let result;
      if (roleId) {
        result = await listEvaluationItemsByRole(tenantDomain, roleId);
      } else {
        result = await listEvaluationItems(tenantDomain);
      }
      setItens(result.items);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Falha ao carregar itens de avaliação';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItens(selectedRoleFilter || undefined);
  }, [tenantDomain, selectedRoleFilter]);

  // Fetch available roles from workforce API
  useEffect(() => {
    const fetchRoles = async () => {
      if (!tenantDomain) return;

      setLoadingRoles(true);
      try {
        const result = await listRoles(tenantDomain, { isActive: true });
        setAvailableRoles(result.roles);
        console.log('📋 Loaded roles for evaluation items:', result.roles.length);
      } catch (err) {
        console.error('Failed to fetch roles:', err);
        // Fallback: show error but don't block the UI
        setError('Falha ao carregar funções. O filtro por função pode não funcionar corretamente.');
      } finally {
        setLoadingRoles(false);
      }
    };

    fetchRoles();
  }, [tenantDomain]);

  // Fetch available role groups from workforce API
  useEffect(() => {
    const fetchGroups = async () => {
      if (!tenantDomain) return;

      setLoadingGroups(true);
      try {
        const groups = await listRoleGroups(tenantDomain);
        setRoleGroups(groups);
        console.log('📋 Loaded role groups:', groups.length);
      } catch (err) {
        console.error('Failed to fetch role groups:', err);
        // Don't block the UI, groups are optional
      } finally {
        setLoadingGroups(false);
      }
    };

    fetchGroups();
  }, [tenantDomain]);

  // Fetch available categories from performance API
  useEffect(() => {
    const fetchCategories = async () => {
      if (!tenantDomain) return;

      setLoadingCategories(true);
      try {
        const result = await listEvaluationCategories(tenantDomain, { status: 'active' });
        setAvailableCategories(result.items);
        console.log('📋 Loaded evaluation categories:', result.items.length);
      } catch (err) {
        console.error('Failed to fetch categories:', err);
        // Don't block the UI, categories can be added manually
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchCategories();
  }, [tenantDomain]);

  // Auto-hide success messages
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleEditItem = (item: EvaluationItem) => {
    setEditingItem(item);
    // Look up proper category name by categoryId to handle legacy lowercase values
    const matchedCategory = availableCategories.find(cat => cat.categoryId === item.categoryId);
    setNewItem({
      category: matchedCategory?.name || item.category,
      description: item.description,
      weight: item.weight,
      applicableRoles: item.applicableRoles,
      applicableRoleGroups: item.applicableRoleGroups || [],
      status: item.status
    });
    setIsEditDialogOpen(true);
  };

  const handleAddItem = () => {
    setEditingItem(null);
    setNewItem({
      category: '',
      description: '',
      weight: 1,
      applicableRoles: [],
      applicableRoleGroups: [],
      status: 'active'
    });
    setIsEditDialogOpen(true);
  };

  const handleSaveItem = async () => {
    if (!tenantDomain) {
      setError('Tenant domain não disponível');
      return;
    }

    // Validation
    if (!newItem.category?.trim() || !newItem.description?.trim()) {
      setError('Por favor, preencha todos os campos obrigatórios.');
      return;
    }

    if (!newItem.applicableRoleGroups || newItem.applicableRoleGroups.length === 0) {
      setError('Selecione pelo menos um grupo de funções.');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      if (editingItem) {
        // Update existing item
        const updates: UpdateEvaluationItemData = {
          category: newItem.category,
          description: newItem.description,
          weight: newItem.weight,
          applicableRoles: newItem.applicableRoles,
          applicableRoleGroups: newItem.applicableRoleGroups,
          status: newItem.status
        };
        await updateEvaluationItem(tenantDomain, editingItem.itemId, updates);
        setSuccessMessage('Item atualizado com sucesso');
      } else {
        // Create new item
        const data: CreateEvaluationItemData = {
          category: newItem.category!,
          description: newItem.description!,
          weight: newItem.weight ?? 1,
          applicableRoles: newItem.applicableRoles || [],
          applicableRoleGroups: newItem.applicableRoleGroups || [],
          status: newItem.status ?? 'active'
        };
        await createEvaluationItem(tenantDomain, data);
        setSuccessMessage('Item criado com sucesso');
      }

      setIsEditDialogOpen(false);
      await fetchItens(selectedRoleFilter || undefined); // Refresh list
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Falha ao salvar item';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteItem = async (itemId: string) => {
    if (!tenantDomain) {
      setError('Tenant domain não disponível');
      return;
    }

    if (!window.confirm('Tem certeza que deseja excluir este item de avaliação?')) {
      return;
    }

    setDeleting(itemId);
    setError(null);
    try {
      await deleteEvaluationItem(tenantDomain, itemId);
      setSuccessMessage('Item excluído com sucesso');
      await fetchItens(selectedRoleFilter || undefined); // Refresh list
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Falha ao excluir item';
      setError(errorMessage);
    } finally {
      setDeleting(null);
    }
  };

  const toggleRole = (roleId: string) => {
    const currentRoles = newItem.applicableRoles || [];
    if (currentRoles.includes(roleId)) {
      setNewItem({
        ...newItem,
        applicableRoles: currentRoles.filter(r => r !== roleId)
      });
    } else {
      setNewItem({
        ...newItem,
        applicableRoles: [...currentRoles, roleId]
      });
    }
  };

  const toggleRoleGroup = (groupName: string) => {
    const currentGroups = newItem.applicableRoleGroups || [];
    if (currentGroups.includes(groupName)) {
      setNewItem({
        ...newItem,
        applicableRoleGroups: currentGroups.filter(g => g !== groupName)
      });
    } else {
      setNewItem({
        ...newItem,
        applicableRoleGroups: [...currentGroups, groupName]
      });
    }
  };

  const isRoleApplicable = (item: EvaluationItem, roleId: string): boolean => {
    // Check direct role match
    if (item.applicableRoles?.includes(roleId)) {
      return true;
    }
    // Check role group match
    const role = availableRoles.find(r => r.roleId === roleId);
    if (role?.roleGroup && item.applicableRoleGroups?.includes(role.roleGroup)) {
      return true;
    }
    return false;
  };

  const getRoleName = (roleId: string): string => {
    return availableRoles.find(r => r.roleId === roleId)?.name || roleId;
  };

  const handleRoleFilterChange = (roleId: string | null) => {
    setSelectedRoleFilter(roleId);
  };

  const clearRoleFilter = () => {
    setSelectedRoleFilter(null);
  };

  // Group filter handlers
  const handleGroupFilterChange = (groupName: string | null) => {
    setSelectedGroupFilter(groupName);
    // Clear role filter when changing group
    setSelectedRoleFilter(null);
  };

  const clearGroupFilter = () => {
    setSelectedGroupFilter(null);
  };

  const getGroupName = (groupName: string): string => {
    return groupName;
  };

  // Filter roles by selected group
  const filteredRoles = selectedGroupFilter
    ? availableRoles.filter(role => role.roleGroup === selectedGroupFilter)
    : availableRoles;

  // Filter items by selected group (using applicableRoleGroups)
  const filteredItens = selectedGroupFilter
    ? itens.filter(item => item.applicableRoleGroups?.includes(selectedGroupFilter))
    : itens;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-brand-gray" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-white text-2xl font-bold">Matriz de Itens de Avaliação</h2>
          <p className="text-brand-gray">Gerencie os critérios de avaliação por função</p>
        </div>

        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={handleAddItem}
              className="bg-success text-black hover:bg-success"
            >
              <Plus className="h-4 w-4 mr-2" />
              Adicionar Item
            </Button>
          </DialogTrigger>

          <DialogContent className="bg-brand-dark-light border-brand-gray/10 text-white">
            <DialogHeader>
              <DialogTitle>
                {editingItem ? 'Editar Item' : 'Adicionar Novo Item'}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label htmlFor="category">Categoria *</Label>
                {loadingCategories ? (
                  <div className="flex items-center gap-2 mt-2 text-brand-gray">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm">Carregando categorias...</span>
                  </div>
                ) : availableCategories.length > 0 ? (
                  <Select
                    value={newItem.category || ''}
                    onValueChange={(value) => setNewItem({...newItem, category: value})}
                  >
                    <SelectTrigger className="bg-brand-dark-lighter border-brand-gray/20 text-white">
                      <SelectValue placeholder="Selecione uma categoria" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableCategories.map(cat => (
                        <SelectItem key={cat.categoryId} value={cat.name}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="mt-2">
                    <Input
                      id="category"
                      value={newItem.category}
                      onChange={(e) => setNewItem({...newItem, category: e.target.value})}
                      className="bg-brand-dark-lighter border-brand-gray/20 text-white"
                      placeholder="Ex: Técnico, Liderança..."
                    />
                    <p className="text-xs text-warning mt-1">
                      Nenhuma categoria cadastrada. Configure categorias na aba "Categorias" ou digite manualmente.
                    </p>
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="description">Descrição *</Label>
                <Textarea
                  id="description"
                  value={newItem.description}
                  onChange={(e) => setNewItem({...newItem, description: e.target.value})}
                  className="bg-brand-dark-lighter border-brand-gray/20 text-white"
                  placeholder="Descreva o critério de avaliação..."
                />
              </div>

              <div>
                <Label htmlFor="weight">Peso (1-5) *</Label>
                <Select
                  value={newItem.weight?.toString()}
                  onValueChange={(value) => setNewItem({...newItem, weight: parseInt(value)})}
                >
                  <SelectTrigger className="bg-brand-dark-lighter border-brand-gray/20 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1,2,3,4,5].map(peso => (
                      <SelectItem key={peso} value={peso.toString()}>{peso}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Aplica aos Grupos de Funções: *</Label>
                <p className="text-xs text-brand-gray mb-2">
                  Selecione os grupos de funções que este critério se aplica
                </p>
                {loadingGroups ? (
                  <div className="flex items-center gap-2 mt-2 text-brand-gray">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm">Carregando grupos...</span>
                  </div>
                ) : roleGroups.length === 0 ? (
                  <div className="mt-2 p-3 bg-warning/10 border border-warning/30 rounded-lg">
                    <p className="text-sm text-warning">
                      Nenhum grupo de funções disponível. Configure grupos no módulo de Workforce.
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3 mt-2 p-3 bg-brand-dark-lighter/50 rounded-lg border border-brand-gray/10">
                    {roleGroups.map(group => (
                      <div key={group} className="flex items-center space-x-2">
                        <Checkbox
                          id={`group-${group}`}
                          checked={newItem.applicableRoleGroups?.includes(group)}
                          onCheckedChange={() => toggleRoleGroup(group)}
                        />
                        <Label htmlFor={`group-${group}`} className="text-sm font-medium text-success">{group}</Label>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Summary of selection */}
              {(newItem.applicableRoleGroups?.length ?? 0) > 0 && (
                <div className="p-3 bg-success/10 border border-success/30 rounded-lg">
                  <p className="text-sm text-success font-medium">Grupos selecionados:</p>
                  <p className="text-xs text-green-300 mt-1">
                    {newItem.applicableRoleGroups?.join(', ')}
                  </p>
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setIsEditDialogOpen(false)}
                  className="border-brand-gray/20 text-white hover:bg-brand-dark-lighter"
                  disabled={submitting}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleSaveItem}
                  className="bg-success text-black hover:bg-success"
                  disabled={submitting || loadingGroups || roleGroups.length === 0}
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : null}
                  Salvar
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive" className="bg-error/20/20 border-error">
          <div className="flex items-center gap-2">
            <span className="text-error"></span>
            <div>
              <p className="text-error font-medium">Erro</p>
              <p className="text-red-300 text-sm">{error}</p>
            </div>
          </div>
        </Alert>
      )}

      {/* Success Alert */}
      {successMessage && (
        <Alert className="bg-success/20/20 border-success">
          <div className="flex items-center gap-2">
            <span className="text-success"></span>
            <div>
              <p className="text-success font-medium">Sucesso</p>
              <p className="text-green-300 text-sm">{successMessage}</p>
            </div>
          </div>
        </Alert>
      )}

      {/* Filters */}
      <Card className="bg-brand-dark-light border-brand-gray/10">
        <CardContent className="p-4">
          <div className="flex items-center gap-6">
            <Filter className="h-5 w-5 text-brand-gray" />

            {/* Role Group Filter */}
            {roleGroups.length > 0 && (
              <div className="flex-1 max-w-xs">
                <Label className="text-white">Filtrar por Grupo:</Label>
                <div className="flex items-center gap-2 mt-2">
                  <Select
                    value={selectedGroupFilter || 'all'}
                    onValueChange={(value) => handleGroupFilterChange(value === 'all' ? null : value)}
                  >
                    <SelectTrigger className="bg-brand-dark-lighter border-brand-gray/20 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos os Grupos</SelectItem>
                      {roleGroups.map(group => (
                        <SelectItem key={group} value={group}>{group}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedGroupFilter && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearGroupFilter}
                      className="text-brand-gray hover:text-white"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* Role Filter (scoped by group if group is selected) */}
            <div className="flex-1 max-w-xs">
              <Label className="text-white">Filtrar por Função:</Label>
              <div className="flex items-center gap-2 mt-2">
                <Select
                  value={selectedRoleFilter || 'all'}
                  onValueChange={(value) => handleRoleFilterChange(value === 'all' ? null : value)}
                >
                  <SelectTrigger className="bg-brand-dark-lighter border-brand-gray/20 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas as Funções</SelectItem>
                    {filteredRoles.map(role => (
                      <SelectItem key={role.roleId} value={role.roleId}>{role.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedRoleFilter && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearRoleFilter}
                    className="text-brand-gray hover:text-white"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Evaluation Items Table */}
      <Card className="bg-brand-dark-light border-brand-gray/10">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-3">
            <span>Itens de Avaliação</span>
            <span className="px-2 py-1 bg-brand-dark-lighter rounded-full text-sm font-normal">
              {filteredItens.length} {filteredItens.length === 1 ? 'item' : 'itens'}
              {selectedGroupFilter && itens.length !== filteredItens.length && (
                <span className="text-brand-gray ml-1">de {itens.length}</span>
              )}
            </span>
            {(selectedGroupFilter || selectedRoleFilter) && (
              <span className="text-sm text-brand-gray font-normal">
                (Filtrando por: {selectedGroupFilter ? `Grupo "${selectedGroupFilter}"` : ''}{selectedGroupFilter && selectedRoleFilter ? ' → ' : ''}{selectedRoleFilter ? getRoleName(selectedRoleFilter) : ''})
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="border-brand-gray/10">
                <TableHead className="text-white">Categoria</TableHead>
                <TableHead className="text-white">Descrição</TableHead>
                <TableHead className="text-white text-center">Peso</TableHead>
                {/* Show role groups when no group filter, show individual roles when group is selected */}
                {selectedGroupFilter ? (
                  // Show individual roles within the selected group
                  filteredRoles.map(role => (
                    <TableHead key={role.roleId} className="text-white text-center text-xs">
                      {role.name}
                    </TableHead>
                  ))
                ) : (
                  // Show role groups
                  roleGroups.map(group => (
                    <TableHead key={group} className="text-white text-center text-xs">
                      {group}
                    </TableHead>
                  ))
                )}
                <TableHead className="text-white text-center">Status</TableHead>
                <TableHead className="text-white text-center">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredItens.map((item) => (
                <TableRow key={item.itemId} className="border-brand-gray/10">
                  <TableCell className="text-white font-medium">{item.category}</TableCell>
                  <TableCell className="text-white max-w-xs">
                    <div className="truncate" title={item.description}>
                      {item.description}
                    </div>
                  </TableCell>
                  <TableCell className="text-white text-center">{item.weight}</TableCell>
                  {/* Show checkboxes for role groups or individual roles */}
                  {selectedGroupFilter ? (
                    // Show checkboxes for individual roles within the selected group
                    filteredRoles.map(role => (
                      <TableCell key={role.roleId} className="text-center">
                        <Checkbox
                          checked={isRoleApplicable(item, role.roleId)}
                          disabled
                        />
                      </TableCell>
                    ))
                  ) : (
                    // Show checkboxes for role groups
                    roleGroups.map(group => (
                      <TableCell key={group} className="text-center">
                        <Checkbox
                          checked={item.applicableRoleGroups?.includes(group)}
                          disabled
                        />
                      </TableCell>
                    ))
                  )}
                  <TableCell className="text-center">
                    <span className={`px-2 py-1 rounded text-xs ${
                      item.status === 'active'
                        ? 'bg-success/20/30 text-success'
                        : 'bg-brand-dark-lighter text-brand-gray'
                    }`}>
                      {item.status === 'active' ? 'Ativo' : 'Inativo'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEditItem(item)}
                        className="text-brand-gray hover:text-white"
                        disabled={deleting === item.itemId}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteItem(item.itemId)}
                        className="text-error hover:text-red-300"
                        disabled={deleting === item.itemId}
                      >
                        {deleting === item.itemId ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Empty state when filter returns no results */}
          {filteredItens.length === 0 && itens.length > 0 && selectedGroupFilter && (
            <div className="flex flex-col items-center justify-center py-12 px-6">
              <div className="w-16 h-16 bg-warning/10 rounded-2xl flex items-center justify-center mb-6">
                <Filter className="h-8 w-8 text-warning" />
              </div>
              <h3 className="text-white text-xl font-display mb-3">
                Nenhum item para o grupo "{selectedGroupFilter}"
              </h3>
              <p className="text-brand-gray text-center max-w-md mb-6">
                Existem {itens.length} itens de avaliação, mas nenhum está configurado para o grupo "{selectedGroupFilter}".
              </p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={clearGroupFilter}
                  className="border-brand-gray/20 text-white hover:bg-brand-dark-lighter"
                >
                  <X className="h-4 w-4 mr-2" />
                  Limpar Filtro
                </Button>
                <Button
                  onClick={handleAddItem}
                  className="bg-success text-black hover:bg-success"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Adicionar Item para {selectedGroupFilter}
                </Button>
              </div>
            </div>
          )}

          {/* Empty state when there are no items at all */}
          {itens.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 px-6">
              {/* Icon Container */}
              <div className="w-16 h-16 bg-success/10 rounded-2xl flex items-center justify-center mb-6">
                <Clipboard className="h-8 w-8 text-success" />
              </div>

              {/* Heading */}
              <h3 className="text-white text-xl font-display mb-3">
                Configure critérios de avaliação
              </h3>

              {/* Supporting Text */}
              <p className="text-brand-gray text-center max-w-2xl mb-8">
                A matriz de itens define os critérios que serão usados para avaliar a performance de cada função.
                Comece adicionando categorias como 'Técnico', 'Liderança', 'Comunicação' e defina quais funções cada critério se aplica.
              </p>

              {/* Setup Steps Box */}
              <div className="bg-brand-dark/50 border border-brand-gray/20 rounded-lg p-6 max-w-2xl w-full mb-6">
                <div className="flex items-center gap-2 mb-4">
                  <Settings className="h-5 w-5 text-success" />
                  <h4 className="text-white font-medium">Como começar:</h4>
                </div>
                <ol className="space-y-3 text-white list-decimal list-inside">
                  <li>Clique em 'Adicionar Item' acima</li>
                  <li>Defina categoria (ex: 'Técnico') e descrição</li>
                  <li>Escolha o peso (1-5) e funções aplicáveis</li>
                  <li>Repita para todos os critérios necessários</li>
                </ol>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  onClick={handleAddItem}
                  className="bg-success text-black hover:bg-success"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Criar Primeiro Critério
                </Button>
                <Button
                  variant="outline"
                  className="border-brand-gray/20 text-white hover:bg-brand-dark-lighter"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Importar Modelo Padrão
                </Button>
              </div>

              {/* Admin Indicator */}
              <div className="flex items-center gap-2 mt-6 text-brand-gray text-xs">
                <Lock className="h-3 w-3" />
                <span>Apenas administradores podem configurar critérios</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MatrizItens;
